/**
 * Convierte un HTML de email a su versión texto plano.
 *
 * - Reemplaza elementos block-level y `<br>` por saltos de línea.
 * - Conserva los hipervínculos como `texto (url)` para que el destinatario
 *   que solo recibe el text/plain pueda igualmente seguirlos.
 * - Strippea el resto de las etiquetas.
 * - Decodifica entidades HTML (`&amp;` → `&`, `&nbsp;` → espacio, etc.) usando
 *   el DOM nativo.
 * - Normaliza espacios y colapsa más de 2 newlines consecutivos a 2.
 *
 * El backend recibe esto como `bodyText` — fallback para clientes de email
 * que prefieren multipart/plain, mejora deliverability (spam scoring).
 */
export function htmlToPlainText(html: string | null | undefined): string {
  if (!html || !html.trim()) return '';

  const doc = new DOMParser().parseFromString(html, 'text/html');

  // Convertir links a "texto (url)" antes de strippear.
  doc.querySelectorAll('a[href]').forEach((a) => {
    const href = a.getAttribute('href') ?? '';
    const text = (a.textContent ?? '').trim();
    const replacement = href && text && text !== href ? `${text} (${href})` : (text || href);
    a.replaceWith(doc.createTextNode(replacement));
  });

  // Forzar salto de línea al final de cada bloque común.
  const blockSelectors = 'p, div, h1, h2, h3, h4, h5, h6, li, tr, blockquote, pre';
  doc.querySelectorAll(blockSelectors).forEach((el) => {
    el.append(doc.createTextNode('\n'));
  });

  // `<br>` → newline (con espacio para que no se pegue al carácter previo).
  doc.querySelectorAll('br').forEach((br) => {
    br.replaceWith(doc.createTextNode('\n'));
  });

  // `<hr>` → línea separadora estilo "----".
  doc.querySelectorAll('hr').forEach((hr) => {
    hr.replaceWith(doc.createTextNode('\n----\n'));
  });

  // textContent ya nos devuelve el texto stripped + entidades decodificadas.
  const raw = (doc.body?.textContent ?? '');

  // Normalizar whitespace:
  //  - colapsar tabs + múltiples espacios en uno solo (sin tocar newlines).
  //  - colapsar 3+ newlines consecutivos a 2 (un párrafo en blanco máx).
  //  - trim final.
  return raw
    .replace(/[ \t]+/g, ' ')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
